/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Fragment, type ReactElement } from 'react';

const TITLE_BR_TAG = /<br\s*\/?>/gi;
const TITLE_BR_ENTITY = /&lt;br\s*\/?&gt;/gi;

/** Business Manager hint for Page Designer `title` string attributes. */
export const PAGE_DESIGNER_TITLE_LINE_BREAK_HINT =
    'Use <br> or <br/> in the text for a manual line break (other HTML is not supported).';

export function normalizeTitleLineBreakMarkers(title: string): string {
    return title.replace(TITLE_BR_ENTITY, '<br>');
}

/** Splits a title on `<br>` / `<br/>` (literal or HTML-entity encoded). */
export function splitTitleLines(title: string): string[] {
    const normalized = normalizeTitleLineBreakMarkers(title);
    return normalized.split(TITLE_BR_TAG).map((segment) => segment.replace(/\s+$/, ''));
}

/** Plain text for SEO, aria-label, and alt fallbacks (line breaks become spaces). */
export function titleToPlainText(title: string): string {
    return splitTitleLines(title.trim())
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim();
}

/** Renders title text with optional `<br>` line breaks (no other HTML). */
export function TitleWithLineBreaks({ text }: { text: string }): ReactElement {
    const lines = splitTitleLines(text);

    if (lines.length <= 1) {
        return <>{text}</>;
    }

    return (
        <>
            {lines.map((line, index) => (
                <Fragment key={index}>
                    {index > 0 ? <br /> : null}
                    {line}
                </Fragment>
            ))}
        </>
    );
}
